import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Modal } from "./Modal";

type Variant = "danger" | "neutral";

interface ConfirmOptions {
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: Variant;
}

interface Toast {
  id: number;
  text: string;
  kind: "error" | "success" | "info";
}

interface NotificationsApi {
  confirm: (opts: ConfirmOptions) => Promise<boolean>;
  toast: (text: string, kind?: Toast["kind"]) => void;
}

const NotificationsContext = createContext<NotificationsApi | null>(null);

export function useNotifications(): NotificationsApi {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error(
      "useNotifications must be used inside <NotificationsProvider>",
    );
  }
  return ctx;
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<
    (ConfirmOptions & { resolve: (ok: boolean) => void }) | null
  >(null);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const confirm = useCallback<NotificationsApi["confirm"]>((opts) => {
    return new Promise<boolean>((resolve) => {
      setConfirmState({ ...opts, resolve });
    });
  }, []);

  const closeConfirm = useCallback(
    (ok: boolean) => {
      if (!confirmState) return;
      confirmState.resolve(ok);
      setConfirmState(null);
    },
    [confirmState],
  );

  const toast = useCallback<NotificationsApi["toast"]>((text, kind = "info") => {
    const id = nextId.current++;
    setToasts((prev) => [...prev, { id, text, kind }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 4500);
  }, []);

  return (
    <NotificationsContext.Provider value={{ confirm, toast }}>
      {children}

      {confirmState && (
        <Modal onClose={() => closeConfirm(false)} maxWidth="26rem">
          <h3 className="text-lg font-black mb-2 pr-6" style={{ color: "#0f0f0f" }}>
            {confirmState.title}
          </h3>
          {confirmState.message && (
            <p className="text-sm whitespace-pre-line" style={{ color: "#71717a" }}>
              {confirmState.message}
            </p>
          )}
          <div className="mt-6 flex gap-3">
            <button
              onClick={() => closeConfirm(true)}
              className="flex-1 rounded-full py-3 text-sm font-semibold text-white transition-all"
              style={{
                backgroundColor:
                  confirmState.variant === "danger" ? "#C61418" : "#111111",
              }}
            >
              {confirmState.confirmLabel ?? "Confirm"}
            </button>
            <button
              onClick={() => closeConfirm(false)}
              className="flex-1 rounded-full py-3 text-sm font-semibold border transition-all"
              style={{ color: "#71717a", borderColor: "rgba(0,0,0,0.14)" }}
            >
              {confirmState.cancelLabel ?? "Cancel"}
            </button>
          </div>
        </Modal>
      )}

      <div
        className="fixed top-4 right-4 z-[60] flex flex-col gap-2 pointer-events-none"
        aria-live="polite"
      >
        {toasts.map((t) => {
          const color =
            t.kind === "error"
              ? "#C61418"
              : t.kind === "success"
                ? "#0f0f0f"
                : "#71717a";
          return (
            <div
              key={t.id}
              className="pointer-events-auto rounded-xl px-4 py-3 text-sm font-semibold shadow-lg max-w-sm"
              style={{
                backgroundColor: "#ffffff",
                border: `1px solid ${color}33`,
                color,
                boxShadow: "0 12px 32px -12px rgba(0,0,0,0.18)",
              }}
            >
              {t.text}
            </div>
          );
        })}
      </div>
    </NotificationsContext.Provider>
  );
}
